/**
 * AdminDailyBlessings — KALKI Daily Divine Blessing Engine control center.
 * Additive. Preserves /admin layout; mounts under /admin/daily-blessings.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { PageHeader, KpiCard, KpiGrid, Panel, RefreshButton,
         INDIGO, MUTE } from "../_shared";
import { api } from "@/hooks/useAuth";
import { toast } from "sonner";

const BRAND = { INDIGO: "#5D00FF", MUTE: "#9999A5", GOOD: "#1C8E54",
                WARN: "#C32E2E", BG: "#F2EEFF" };

function Pill({ children, kind = "neutral" }) {
  const colors = { good: "#1C8E54", warn: "#C32E2E",
                   neutral: INDIGO, muted: MUTE };
  return (
    <span style={{ background: colors[kind] + "22",
                    color: colors[kind], borderRadius: 999,
                    padding: "3px 10px", fontSize: 11, fontWeight: 600,
                    letterSpacing: "0.08em", textTransform: "uppercase" }}>
      {children}
    </span>
  );
}

export default function AdminDailyBlessings() {
  const [overview, setOverview] = useState(null);
  const [automation, setAutomation] = useState(null);
  const [q, setQ] = useState("");
  const [subscribedOnly, setSubscribedOnly] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");

  const loadOverview = useCallback(async () => {
    try {
      const [o, a] = await Promise.all([
        api.get("/mail/admin/daily-blessing/overview"),
        api.get("/mail/admin/daily-blessing/automation"),
      ]);
      setOverview(o.data || o);
      setAutomation(a.data || a);
    } catch (e) {
      toast.error("Overview load failed: " + (e?.message || e));
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({ limit: "40", skip: "0" });
      if (q) params.set("q", q);
      if (subscribedOnly) params.set("subscribed_only", "true");
      const r = await api.get("/mail/admin/daily-blessing/users?" + params.toString());
      setUsers((r.data || r).items || []);
    } catch (e) {
      toast.error("Users load failed");
    } finally {
      setLoadingUsers(false);
    }
  }, [q, subscribedOnly]);

  useEffect(() => { loadOverview(); loadUsers(); }, [loadOverview, loadUsers]);

  const loadDetail = useCallback(async (uid) => {
    setSelected(uid); setDetail(null); setConfirmSend(false); setConfirmEmail("");
    try {
      const r = await api.get(`/mail/admin/daily-blessing/user/${encodeURIComponent(uid)}`);
      setDetail(r.data || r);
    } catch (e) {
      toast.error("User detail load failed");
    }
  }, []);

  const doGenerate = useCallback(async (regen = false) => {
    if (!selected) return;
    setGenerating(true);
    try {
      const path = regen ? "regenerate" : "generate";
      const r = await api.post(
        `/mail/admin/daily-blessing/user/${encodeURIComponent(selected)}/${path}`,
        {});
      toast.success(regen ? "Draft regenerated." : "Draft loaded.");
      setDetail((d) => ({ ...(d || {}), draft: (r.data || r).draft }));
    } catch (e) {
      toast.error("Generation failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setGenerating(false);
    }
  }, [selected]);

  const doTestSend = useCallback(async () => {
    if (!selected) return;
    try {
      await api.post(`/mail/admin/daily-blessing/user/${encodeURIComponent(selected)}/test-send`, {});
      toast.success("Test copy enqueued to your admin inbox.");
    } catch (e) {
      toast.error("Test send failed: " + (e?.response?.data?.detail || e?.message));
    }
  }, [selected]);

  const doSubscribe = useCallback(async (nextSubscribed) => {
    if (!selected) return;
    try {
      await api.post(
        `/mail/admin/daily-blessing/user/${encodeURIComponent(selected)}/${nextSubscribed ? "subscribe" : "unsubscribe"}`,
        {});
      toast.success(nextSubscribed ? "User subscribed." : "User unsubscribed.");
      await loadDetail(selected);
      await loadOverview();
      await loadUsers();
    } catch (e) {
      toast.error("Subscription change failed: " + (e?.response?.data?.detail || e?.message));
    }
  }, [selected, loadDetail, loadOverview, loadUsers]);

  const doManualSend = useCallback(async () => {
    if (!selected || !detail?.user?.email) return;
    if (confirmEmail.trim().toLowerCase() !== (detail.user.email || "").trim().toLowerCase()) {
      toast.error("Recipient email confirmation does not match.");
      return;
    }
    setSending(true);
    try {
      await api.post(
        `/mail/admin/daily-blessing/user/${encodeURIComponent(selected)}/send`,
        { confirm_email: confirmEmail.trim() });
      toast.success("Blessing enqueued for delivery.");
      setConfirmSend(false); setConfirmEmail("");
      await loadDetail(selected);
    } catch (e) {
      toast.error("Send failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSending(false);
    }
  }, [selected, detail, confirmEmail, loadDetail]);

  const toggleAutomation = useCallback(async () => {
    const next = !(automation?.enabled);
    const msg = next
      ? "Enable automated daily 9 AM IST sends to ALL subscribers? Make sure consent + rate limits are verified."
      : "Pause automated daily sends? Previews and manual sends will still work.";
    if (!window.confirm(msg)) return;
    try {
      await api.put("/mail/admin/daily-blessing/automation",
                     { enabled: next, note: next ? "enabled by admin" : "paused by admin" });
      toast.success(next ? "Automation enabled." : "Automation paused.");
      loadOverview();
    } catch (e) {
      toast.error("Automation toggle failed: " + (e?.response?.data?.detail || e?.message));
    }
  }, [automation, loadOverview]);

  const draft = detail?.draft;
  const content = draft?.content;

  return (
    <div data-testid="admin-daily-blessings">
      <PageHeader title="Daily Divine Blessing"
                   subtitle="Personalized 9 AM IST spiritual email · preview, test, send">
        <RefreshButton onClick={loadOverview} />
      </PageHeader>

      {/* ----- Automation banner ----- */}
      <Panel data-testid="dblessing-automation-panel"
             style={{ marginBottom: 16,
                      background: automation?.enabled ? "#E7F7EE" : "#FFF4E5" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center",
                       flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 220px" }}>
            <div style={{ fontSize: 11, letterSpacing: "0.14em",
                           color: INDIGO, fontWeight: 600,
                           textTransform: "uppercase" }}>
              AUTOMATION STATUS
            </div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#1C1C1C",
                           marginTop: 4 }}>
              {automation?.enabled
                ? "Enabled — scheduled 9 AM IST sends run daily."
                : "Paused — only manual admin previews/sends are active."}
            </div>
            {automation?.updated_by && (
              <div style={{ fontSize: 11, color: MUTE, marginTop: 4 }}>
                Last change: {automation.updated_by} · {automation.updated_at?.slice(0, 19)}
              </div>
            )}
          </div>
          <button data-testid="dblessing-automation-toggle"
                   onClick={toggleAutomation}
                   style={{ padding: "10px 18px", borderRadius: 999, border: 0,
                             background: automation?.enabled ? BRAND.WARN : INDIGO,
                             color: "white", fontSize: 12, fontWeight: 600,
                             letterSpacing: "0.14em", textTransform: "uppercase",
                             cursor: "pointer" }}>
            {automation?.enabled ? "Pause Automation" : "Enable Automation"}
          </button>
        </div>
      </Panel>

      {/* ----- KPI grid ----- */}
      <KpiGrid>
        <KpiCard testId="dblessing-kpi-subs" label="Subscribers"
                  value={overview?.subscribers ?? "—"}
                  hint="Opt-in users ready for 9 AM IST send" />
        <KpiCard testId="dblessing-kpi-drafts" label="Drafts Today"
                  value={overview?.drafts_today ?? "—"}
                  hint={`${overview?.today_ist || ""} (IST)`} />
        <KpiCard testId="dblessing-kpi-fallback" label="Fallback Today"
                  value={overview?.fallback_today ?? "—"}
                  hint="AI validation failed → safe template" />
        <KpiCard testId="dblessing-kpi-sent-today" label="Queued Today"
                  value={overview?.deliveries_today ?? "—"}
                  hint="Scheduled or manual sends" />
        <KpiCard testId="dblessing-kpi-sent-yday" label="Yesterday"
                  value={overview?.deliveries_yesterday ?? "—"}
                  hint="24h ago (IST)" />
      </KpiGrid>

      {/* ----- Two column: user search + preview ----- */}
      <div style={{ display: "grid",
                     gridTemplateColumns: "minmax(260px, 1fr) minmax(0, 2.4fr)",
                     gap: 16, marginTop: 16 }}>
        <Panel data-testid="dblessing-user-panel">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap",
                         marginBottom: 10 }}>
            <input data-testid="dblessing-search-input"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name / email / user_id"
                    style={{ flex: "1 1 160px", padding: "8px 10px",
                              borderRadius: 10, border: "1px solid #E4E0F0",
                              fontSize: 13 }} />
            <button data-testid="dblessing-search-btn"
                     onClick={loadUsers}
                     style={{ padding: "8px 14px", borderRadius: 10,
                               border: 0, background: INDIGO, color: "white",
                               fontSize: 12, fontWeight: 600,
                               cursor: "pointer" }}>Search</button>
          </div>
          <label style={{ display: "inline-flex", alignItems: "center",
                           gap: 6, fontSize: 12, color: "#1C1C1C",
                           marginBottom: 10 }}>
            <input data-testid="dblessing-subs-only"
                    type="checkbox" checked={subscribedOnly}
                    onChange={(e) => { setSubscribedOnly(e.target.checked); }} />
            Subscribers only
          </label>
          <div style={{ maxHeight: 540, overflow: "auto",
                         borderRadius: 10, border: "1px solid #EEEBF5" }}>
            {loadingUsers ? (
              <div style={{ padding: 16, color: MUTE, fontSize: 13 }}>Loading…</div>
            ) : users.length === 0 ? (
              <div style={{ padding: 16, color: MUTE, fontSize: 13 }}>No users.</div>
            ) : users.map((u) => (
              <button key={u.user_id}
                       data-testid={`dblessing-user-row-${u.user_id}`}
                       onClick={() => loadDetail(u.user_id)}
                       style={{ display: "block", width: "100%",
                                 textAlign: "left", padding: "10px 12px",
                                 background: selected === u.user_id
                                                ? BRAND.BG : "white",
                                 border: 0,
                                 borderBottom: "1px solid #EEEBF5",
                                 cursor: "pointer", fontSize: 13 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center",
                               justifyContent: "space-between" }}>
                  <strong style={{ color: "#1C1C1C" }}>
                    {u.name || u.email || u.user_id}
                  </strong>
                  <Pill kind={u.subscribed ? "good" : "muted"}>
                    {u.subscribed ? "SUBSCRIBED" : "OPT-OUT"}
                  </Pill>
                </div>
                <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>
                  {u.email} · {u.language || "en"}
                </div>
              </button>
            ))}
          </div>
        </Panel>

        <Panel data-testid="dblessing-preview-panel">
          {!selected ? (
            <div style={{ padding: 32, textAlign: "center", color: MUTE,
                           fontSize: 14 }}>
              Select a user on the left to view their personalized
              blessing for today.
            </div>
          ) : !detail ? (
            <div style={{ padding: 24, color: MUTE }}>Loading…</div>
          ) : (
            <div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10,
                             alignItems: "flex-start",
                             marginBottom: 12 }}>
                <div style={{ flex: "1 1 220px" }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.26em",
                                 color: INDIGO, fontWeight: 600 }}>DEVOTEE</div>
                  <div style={{ fontSize: 18, fontWeight: 500, color: "#1C1C1C" }}>
                    {detail.user.name || detail.user.email}
                  </div>
                  <div style={{ fontSize: 12, color: MUTE }}>
                    {detail.user.email} · {detail.preferences?.language || "en"} ·
                    {" "}<Pill kind={detail.preferences?.subscribed ? "good" : "muted"}>
                      {detail.preferences?.subscribed ? "SUBSCRIBED" : "OPT-OUT"}
                    </Pill>
                  </div>
                  {!detail.eligible && (
                    <div style={{ marginTop: 6, fontSize: 11,
                                   color: BRAND.WARN, fontWeight: 600 }}>
                      Ineligible: {detail.ineligibility_reason}
                    </div>
                  )}
                  {detail.preferences?.subscribed ? (
                    <button data-testid="dblessing-unsub-user-btn"
                             onClick={() => doSubscribe(false)}
                             style={{ marginTop: 8, padding: "6px 12px",
                                       borderRadius: 999,
                                       border: "1px solid " + MUTE,
                                       background: "white", color: MUTE,
                                       fontSize: 11, fontWeight: 600,
                                       letterSpacing: "0.1em",
                                       cursor: "pointer" }}>
                      UNSUBSCRIBE THIS USER
                    </button>
                  ) : (
                    <button data-testid="dblessing-sub-user-btn"
                             onClick={() => doSubscribe(true)}
                             style={{ marginTop: 8, padding: "6px 12px",
                                       borderRadius: 999, border: 0,
                                       background: BRAND.GOOD, color: "white",
                                       fontSize: 11, fontWeight: 600,
                                       letterSpacing: "0.1em",
                                       cursor: "pointer" }}>
                      SUBSCRIBE THIS USER
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button data-testid="dblessing-generate-btn"
                           disabled={generating}
                           onClick={() => doGenerate(false)}
                           style={{ padding: "8px 14px", borderRadius: 10,
                                     border: "1px solid " + INDIGO,
                                     background: "white", color: INDIGO,
                                     fontSize: 12, fontWeight: 600,
                                     cursor: "pointer" }}>
                    {draft ? "Reload Draft" : "Generate Today's Blessing"}
                  </button>
                  <button data-testid="dblessing-regenerate-btn"
                           disabled={generating || !draft}
                           onClick={() => doGenerate(true)}
                           style={{ padding: "8px 14px", borderRadius: 10,
                                     border: "1px solid " + INDIGO,
                                     background: "white", color: INDIGO,
                                     fontSize: 12, fontWeight: 600,
                                     cursor: "pointer" }}>
                    Regenerate
                  </button>
                  <button data-testid="dblessing-test-send-btn"
                           onClick={doTestSend}
                           disabled={!draft}
                           style={{ padding: "8px 14px", borderRadius: 10,
                                     border: 0, background: "#F2EEFF",
                                     color: INDIGO, fontSize: 12,
                                     fontWeight: 600, cursor: "pointer" }}>
                    Test to Admin
                  </button>
                  <button data-testid="dblessing-send-btn"
                           disabled={!draft || !detail.eligible}
                           onClick={() => setConfirmSend((c) => !c)}
                           style={{ padding: "8px 14px", borderRadius: 10,
                                     border: 0, background: INDIGO,
                                     color: "white", fontSize: 12,
                                     fontWeight: 600, cursor: "pointer",
                                     opacity: (!draft || !detail.eligible) ? 0.5 : 1 }}>
                    Send to This User
                  </button>
                </div>
              </div>

              {confirmSend && (
                <div data-testid="dblessing-confirm-panel"
                      style={{ background: "#FFF4E5", padding: 12,
                                borderRadius: 10, marginBottom: 12,
                                fontSize: 13 }}>
                  <div style={{ marginBottom: 6, color: "#8A5A00",
                                 fontWeight: 600 }}>
                    Confirm recipient email to send:
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input data-testid="dblessing-confirm-email"
                            value={confirmEmail}
                            onChange={(e) => setConfirmEmail(e.target.value)}
                            placeholder={detail.user.email}
                            style={{ flex: "1 1 180px", padding: "8px 10px",
                                      borderRadius: 10, border: "1px solid #E4E0F0",
                                      fontSize: 13 }} />
                    <button data-testid="dblessing-confirm-send"
                             disabled={sending}
                             onClick={doManualSend}
                             style={{ padding: "8px 14px", borderRadius: 10,
                                       border: 0, background: BRAND.WARN,
                                       color: "white", fontSize: 12,
                                       fontWeight: 600, cursor: "pointer" }}>
                      {sending ? "Sending…" : "Confirm & Send"}
                    </button>
                  </div>
                </div>
              )}

              {!draft ? (
                <div style={{ padding: 24, textAlign: "center",
                               color: MUTE, fontSize: 13 }}>
                  No draft generated for today yet. Click "Generate Today's Blessing".
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap",
                                 marginBottom: 10, fontSize: 12 }}>
                    <Pill kind={draft.source === "llm" ? "good" : "warn"}>
                      SOURCE: {draft.source}
                    </Pill>
                    <Pill>THEME: {content?.daily_theme || "—"}</Pill>
                    <Pill>LANG: {content?.language || "en"}</Pill>
                    <Pill>SEGMENT: {draft.context?.segment || "—"}</Pill>
                    {Array.isArray(draft.validation_issues) &&
                      draft.validation_issues.length > 0 && (
                        <Pill kind="warn">
                          {draft.validation_issues.length} VALIDATION FLAGS
                        </Pill>
                    )}
                  </div>
                  <div style={{ fontSize: 11, letterSpacing: "0.14em",
                                 color: MUTE, marginBottom: 6 }}>
                    SUBJECT
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "#1C1C1C",
                                 marginBottom: 12 }}>
                    {content?.subject}
                  </div>
                  <div style={{ fontSize: 11, letterSpacing: "0.14em",
                                 color: MUTE, marginBottom: 6 }}>
                    PREVIEW TEXT
                  </div>
                  <div style={{ fontSize: 13, color: "#676773", marginBottom: 14,
                                 fontStyle: "italic" }}>
                    {content?.preview_text}
                  </div>
                  <div style={{ border: "1px solid #EEEBF5", borderRadius: 14,
                                 overflow: "hidden" }}>
                    <iframe data-testid="dblessing-html-preview"
                             title="Daily Blessing preview"
                             srcDoc={draft.html}
                             style={{ width: "100%", height: 720, border: 0,
                                       background: "#FAFAFC" }} />
                  </div>

                  {detail.history && detail.history.length > 0 && (
                    <div style={{ marginTop: 18 }}>
                      <div style={{ fontSize: 11, letterSpacing: "0.14em",
                                     color: MUTE, marginBottom: 8 }}>
                        DELIVERY HISTORY
                      </div>
                      <table data-testid="dblessing-history-table"
                              style={{ width: "100%", fontSize: 12,
                                        borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ color: MUTE, textAlign: "left" }}>
                            <th style={{ padding: "6px 8px" }}>Date (IST)</th>
                            <th style={{ padding: "6px 8px" }}>State</th>
                            <th style={{ padding: "6px 8px" }}>Source</th>
                            <th style={{ padding: "6px 8px" }}>Subject</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.history.map((h) => (
                            <tr key={h.idempotency_key}
                                 style={{ borderTop: "1px solid #EEEBF5" }}>
                              <td style={{ padding: "6px 8px" }}>{h.ist_date}</td>
                              <td style={{ padding: "6px 8px" }}>
                                <Pill kind={h.state === "queued" ? "good"
                                              : h.state === "duplicate" ? "warn"
                                              : "muted"}>
                                  {h.state}
                                </Pill>
                              </td>
                              <td style={{ padding: "6px 8px" }}>{h.source}</td>
                              <td style={{ padding: "6px 8px",
                                            maxWidth: 260,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis" }}>
                                {h.subject}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
